<!--
  Read-page record summary — the v-data-table-era replacement for
  BreadTabulatorDataView.

  The component it replaces rendered `model.read.table.headers` as a two-column
  <v-simple-table> and carried two bugs worth naming, because both are fixed
  here:

    1. The parent-relation branch had no `v-else` on the plain <td> that
       followed it, so a `dataType: "model"` row rendered TWO cells — the
       parent's name and a blank one.
    2. Every value was read as `record[field]`, which returns undefined for the
       dotted fields the models use (`_count.Something`). Resolution goes
       through the same getFieldValue() the table cells use.

  Values are always rendered as TEXT. Unlike a table cell there is no formatter
  contract here, so nothing a model or the database supplies is ever treated as
  markup.

  Layout is label-left / value-right rows. It was briefly a wrapping card grid,
  which had one flaw a list cannot have: the last row was padded out with an
  empty cell whenever the field count didn't divide by the column count.
-->
<template>
  <dl class="brv">
    <div v-for="item in items" :key="item.key" class="brv__row">
      <dt class="brv__label">{{ item.label }}</dt>
      <dd class="brv__value" :class="{ 'brv__value--empty': item.isEmpty }">
        {{ item.value }}
      </dd>
    </div>
  </dl>
</template>

<script>
import { getFieldValue } from "./breadDataTable/fieldValue";

/** Anything the API hands back that is really a timestamp, however it's typed. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}|$)/;

export default {
  name: "BreadDataRecordView",
  props: {
    /** The record itself — `api.data[0]` on the read page. */
    record: {
      type: Object,
      default: () => ({}),
    },
    /** `model.read.table.headers`. */
    headers: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    items() {
      return this.headers.map((header, i) => {
        const raw = this.resolve(header);
        const isEmpty = raw === null || raw === undefined || raw === "";
        return {
          key: header.field || `__${i}__`,
          label: header.title || header.text || header.field || "",
          value: isEmpty ? "—" : this.format(header, raw),
          isEmpty,
        };
      });
    },
  },
  methods: {
    resolve(header) {
      const record = this.record || {};

      if (header.dataType === "model" && header.relationship === "parent") {
        const parent = record[this.$_ucfirst(header.modelKey)];
        if (!parent) return null;
        return parent.name != null ? parent.name : parent.id;
      }

      return getFieldValue(record, header.field);
    },
    format(header, value) {
      if (typeof value === "boolean") return value ? "Yes" : "No";

      const looksLikeDate =
        header.dataType === "date" ||
        /^timestamp/i.test(header.field || "") ||
        (typeof value === "string" && ISO_DATE.test(value));

      if (looksLikeDate) {
        const m = this.$moment(new Date(value));
        if (m.isValid()) return m.format("DD MMM YYYY, HH:mm");
      }

      if (value !== null && typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
  },
};
</script>

<style scoped>
/* Colour is token-only (--app-* / --tbl-*, defined in theme-dark.css and
   table-tokens.css), so light and dark are both covered without a second
   `.dark-mode` block to keep in sync. */
/* A definition LIST, not a grid: label left, value right, one field per row.
   The grid this replaces stretched its last row into an empty filler cell
   whenever the field count didn't divide evenly by the column count — rows
   can't produce that, and a record with two fields reads the same as one with
   twelve. */
.brv {
  margin: 0;
  padding: 0;
  max-width: 720px;
}

.brv__row {
  display: flex;
  align-items: baseline;
  gap: 24px;
  padding: 13px 2px;
  border-bottom: 1px solid var(--app-border);
}

/* No trailing rule: the last row's border would read as the panel's own edge
   and the panel doesn't have one here. */
.brv__row:last-child {
  border-bottom: none;
}

.brv__label {
  flex: 0 0 auto;
  width: 190px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--app-text-muted);
  margin: 0;
}

/* Right-aligned so every value lands on one edge — the eye scans a single
   column instead of a ragged one that restarts after each label. */
.brv__value {
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
  font-family: var(--tbl-font);
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
  /* Long values (uuids, emails) break rather than widening the row. */
  overflow-wrap: anywhere;
}

.brv__value--empty {
  color: var(--app-text-faint);
  font-weight: 500;
}

/* Right-alignment needs a facing edge to align against. Below ~600px the row
   is too narrow for one, so the pair stacks and both sides go left. */
@media (max-width: 599px) {
  .brv__row {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    padding: 11px 2px;
  }

  .brv__label {
    width: auto;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--app-text-faint);
  }

  .brv__value {
    text-align: left;
  }
}
</style>
